import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import React from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button, IconButton, Switch, Tooltip } from "@mui/material";
import { Add, InfoOutlined, ModeEditOutlineTwoTone } from "@mui/icons-material";
import AppTable from "components/Shared/AppTable";
import TableFilter from "components/Shared/AppTable/TableFilter";
import { useAppMeta, useAuth, useTablePagination } from "hooks";
import HighlightedText from "components/Shared/HighlightedText";
import CustomTabs from "components/Shared/CustomTabs";
import { toast } from "react-toastify";
import {
  useLazyGetMunicipalityListQuery,
  useUpdateMunicipalityStatusMutation,
} from "store/services/LocationsApi/municipalityApi";

const MunicipalityListScreen = () => {
  const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
    useTablePagination();

  const [searchParams] = useSearchParams();
  const keyword = searchParams.get("search") || "";

  const breadcrumbs = [{ title: "Municipality" }];

  const activeTab = searchParams.get("tab") || "Active";

  const { isSystemAdmin } = useAuth();

  const [updateMunicipalitytatus] = useUpdateMunicipalityStatusMutation();

  const municipalityListTabs = [
    { label: "Active", value: "Active" },
    { label: "Inactive", value: "Inactive" },
  ];
  // status update
  const handleStatusChange = (row) => {
    const newStatus = row.status === "Active" ? "Inactive" : "Active";

    const isConfirmed = window.confirm(
      `Are you sure you want to change the status to ${newStatus}?`
    );

    if (isConfirmed) {
      updateMunicipalitytatus({ id: row._id, status: newStatus })
        .unwrap()
        .then(() => {
          toast.success(`Municipality status updated to ${newStatus}`);
        })
        .catch((error) => {
          const errorMessage = error?.data?.message || "Something went wrong!";
          toast.error(errorMessage);
        });
    }
  };

  const [
    fetchMunicipality,
    {
      data: municipalityRes,
      isLoading: loadingMunicipality,
      isFetching: fetchingMunicipality,
      error,
    },
  ] = useLazyGetMunicipalityListQuery();

  const columns = [
    {
      name: "Municipality Name",
      selector: (row) => row?.name,
      cell: (row) =>
        row?.name ? (
          <HighlightedText text={row?.name} highlight={keyword} />
        ) : (
          "N/A"
        ),
      sortable: true,
    },
    {
      name: " Municipality Code",
      selector: (row) => row?.code,
      sortable: true,
    },
    {
      name: "Region",
      selector: (row) => row?.region?.name || "N/A",
      sortable: true,
    },
    {
      name: "District",
      selector: (row) => row?.district?.name,
      sortable: true,
    },

    {
      name: "Actions",
      cell: (row) => (
        <>
          <Link to={`/app/crm/municipality/${row._id}/detail`}>
            <Tooltip title={"View Detail"} arrow>
              <IconButton size="small" aria-label="View Branch" color="primary">
                <InfoOutlined />
              </IconButton>
            </Tooltip>
          </Link>
          <Link
            to={`/app/crm/municipality/${row._id}/edit`}
            state={{ from: "list" }}
          >
            <Tooltip title={"Edit"} arrow>
              <IconButton size="small" aria-label="Edit Branch">
                {/* <ModeEditOutlineTwoTone /> */}
              </IconButton>
            </Tooltip>
          </Link>
          {false && (
            <Tooltip
              title={row.status === "Active" ? "Deactivate" : "Activate"}
              arrow
            >
              <Switch
                checked={row.status === "Active"}
                onChange={() => handleStatusChange(row)}
                color="primary"
                inputProps={{ "aria-label": "Status Toggle" }}
              />
            </Tooltip>
          )}
        </>
      ),
    },
  ];
  React.useEffect(() => {
    fetchMunicipality({
      status: activeTab,
      page: currentPage,
      limit: perPage,
      ...(keyword && { search: keyword }),
    });
  }, [activeTab, fetchMunicipality, currentPage, keyword, perPage]);

  useAppMeta(`${activeTab} Municipality`);

  return (
    <BreadcrumbLayout
      breadcrumbs={breadcrumbs}
      headerActions={isSystemAdmin && <PageActions />}
    >
      <BreadcrumbLayout.Error error={error} />
      <BreadcrumbLayout.Paper>
        <CustomTabs tabs={municipalityListTabs} activeTab={activeTab} />

        <>
          <TableFilter
            searchLabel="Name"
            disabled={loadingMunicipality || fetchingMunicipality}
          >
            <div className="font-medium">{activeTab} Municipality</div>
          </TableFilter>
          <AppTable
            columns={columns}
            data={municipalityRes?.data ?? []}
            progressPending={loadingMunicipality || fetchingMunicipality}
            paginationServer
            paginationPerPage={perPage}
            paginationTotalRows={municipalityRes?.totalItems ?? 0}
            paginationDefaultPage={currentPage}
            onChangePage={handlePageChange}
            onChangeRowsPerPage={handlePerRowsChange}
          />
        </>
      </BreadcrumbLayout.Paper>
    </BreadcrumbLayout>
  );
};

const PageActions = () => {
  return (
    <div className="flex space-x-4">
      <Link to="/app/crm/municipality/create">
        <Button variant="contained" startIcon={<Add />}>
          New municipality
        </Button>
      </Link>
    </div>
  );
};

export default MunicipalityListScreen;
